<script setup>
// NOTICE: KAPTION-INTEGRATION - WhatsApp Direct Send page
// Manages direct campaigns for sending template messages to individual contacts
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { canUseFeature } = useFeaturePermissions();

if (!canUseFeature('whatsapp_direct_send')) {
  router.replace({ name: 'campaigns_whatsapp_index' });
}

const newDirectRoute = computed(() => ({
  name: 'campaigns_direct_create',
  params: { accountId: route.params.accountId },
}));

const navigateToCreate = () => {
  if (!canUseFeature('whatsapp_direct_send')) return;
  router.push({ name: 'campaigns_direct_create' });
};

const navigateToReport = directId => {
  router.push({ name: 'campaigns_direct_report', params: { directId } });
};

const directCampaigns = ref([]);
const isLoading = ref(false);
const selectedInboxId = ref(null);

// NOTICE: KAPTION-INTEGRATION - Archive filter state for direct messages
const archiveFilter = ref('active');

const whatsappInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes.filter(inbox => inbox.channel_type === 'Channel::Whatsapp');
});

const fetchDirectCampaigns = async () => {
  isLoading.value = true;
  try {
    const accountId = route.params.accountId;
    const params = selectedInboxId.value
      ? { inbox_id: selectedInboxId.value }
      : {};
    // NOTICE: KAPTION-INTEGRATION - Pass archive filter params to API
    if (archiveFilter.value === 'archived') {
      params.archived_only = true;
    } else if (archiveFilter.value === 'all') {
      params.include_archived = true;
    }
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId}/direct_messages`,
      { params }
    );
    directCampaigns.value = response.data.data || [];
  } catch (error) {
    directCampaigns.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleInboxChange = inboxId => {
  selectedInboxId.value = inboxId;
  fetchDirectCampaigns();
};

// NOTICE: KAPTION-INTEGRATION - Archive filter change handler
const handleArchiveFilterChange = filter => {
  archiveFilter.value = filter;
  fetchDirectCampaigns();
};

// NOTICE: KAPTION-INTEGRATION - Archive/Unarchive handlers for direct campaigns
const handleArchive = async campaign => {
  try {
    const accountId = route.params.accountId;
    await window.axios.patch(
      `/api/v1/accounts/${accountId}/direct_messages/${campaign.id}/archive`
    );
    // Remove from local list after archiving
    directCampaigns.value = directCampaigns.value.filter(
      c => c.id !== campaign.id
    );
  } catch (error) {
    // Error silently handled
  }
};

const handleUnarchive = async campaign => {
  try {
    const accountId = route.params.accountId;
    await window.axios.patch(
      `/api/v1/accounts/${accountId}/direct_messages/${campaign.id}/unarchive`
    );
    // Remove from local list after unarchiving
    directCampaigns.value = directCampaigns.value.filter(
      c => c.id !== campaign.id
    );
  } catch (error) {
    // Error silently handled
  }
};

onMounted(() => {
  if (!canUseFeature('whatsapp_direct_send')) return;
  store.dispatch('inboxes/get');
  fetchDirectCampaigns();
});
</script>

<template>
  <CampaignLayout :header-title="t('DIRECT.TITLE')">
    <!-- NOTICE: KAPTION-INTEGRATION - New Direct Message button -->
    <template #header-action>
      <router-link
        :to="newDirectRoute"
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-hover rounded-lg transition-colors shadow-sm"
      >
        <span class="i-lucide-plus w-4 h-4" />
        {{ t('DIRECT.CREATE.NEW_BUTTON') }}
      </router-link>
    </template>

    <!-- NOTICE: KAPTION-INTEGRATION - Archive filter + sorting controls (same row) -->
    <div v-if="!isLoading" class="flex items-center justify-between gap-2 py-2">
      <!-- NOTICE: KAPTION-INTEGRATION - Archive filter buttons (left) -->
      <div class="flex items-center gap-2">
        <button
          v-for="filter in ['active', 'archived', 'all']"
          :key="filter"
          class="px-3 py-1 text-sm rounded-md transition-colors"
          :class="[
            archiveFilter === filter
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
          ]"
          @click="handleArchiveFilterChange(filter)"
        >
          {{ t(`CAMPAIGN.ARCHIVE.FILTER.${filter.toUpperCase()}`) }}
        </button>
      </div>
      <!-- Inbox filter + count (right) -->
      <div class="flex items-center gap-2">
        <span v-if="directCampaigns.length > 0" class="text-sm text-n-slate-11">
          {{ directCampaigns.length }} {{ t('DIRECT.ITEMS') }}
        </span>
        <select
          class="!mb-0 !w-auto text-sm"
          @change="handleInboxChange($event.target.value)"
        >
          <option value="">
            {{ t('DIRECT.ALL_INBOXES') }}
          </option>
          <option
            v-for="inbox in whatsappInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <div
      v-else-if="directCampaigns.length === 0"
      class="flex flex-col items-center justify-center py-20 gap-4"
    >
      <div class="i-lucide-send text-4xl text-n-slate-8" />
      <h3 class="text-lg font-medium text-n-slate-11">
        {{ t('DIRECT.EMPTY.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10 text-center max-w-md">
        {{ t('DIRECT.EMPTY.DESCRIPTION') }}
      </p>
      <Button
        :label="t('DIRECT.CREATE.NEW_BUTTON')"
        icon="i-lucide-plus"
        @click="navigateToCreate"
      />
    </div>

    <div v-else class="flex flex-col gap-3 pt-1">
      <div
        v-for="campaign in directCampaigns"
        :key="campaign.id"
        class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-alpha-black2 hover:bg-n-alpha-black4 transition-colors cursor-pointer"
        :class="campaign.campaign_status === 'archived' ? 'opacity-60' : ''"
        @click="navigateToReport(campaign.id)"
      >
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ campaign.title }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ campaign.template_params?.name }} ({{
              campaign.template_params?.language
            }})
          </span>
        </div>
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-black4 transition-colors"
            :title="t('CAMPAIGN.REPORT.VIEW_REPORT')"
            @click.stop="navigateToReport(campaign.id)"
          >
            <span class="i-lucide-bar-chart-2 w-4 h-4" />
          </button>
          <!-- NOTICE: KAPTION-INTEGRATION - Archive/Unarchive buttons for direct campaigns -->
          <button
            v-if="campaign.campaign_status === 'archived'"
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-black4 transition-colors"
            :title="t('CAMPAIGN.ARCHIVE.UNARCHIVE')"
            @click.stop="handleUnarchive(campaign)"
          >
            <span class="i-lucide-archive-restore w-4 h-4" />
          </button>
          <button
            v-else
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-black4 transition-colors"
            :title="t('CAMPAIGN.ARCHIVE.ARCHIVE')"
            @click.stop="handleArchive(campaign)"
          >
            <span class="i-lucide-archive w-4 h-4" />
          </button>
          <span class="text-xs text-n-slate-9">
            {{ new Date(campaign.created_at).toLocaleDateString() }}
          </span>
          <!-- NOTICE: KAPTION-INTEGRATION - DRY-RUN badge (sends simulated, no delivery) -->
          <span
            v-if="campaign.trigger_rules?.dry_run"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
          >
            {{ t('DIRECT.DRY_RUN') }}
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded-full"
            :class="
              campaign.campaign_status === 'archived'
                ? 'bg-n-alpha-black4 text-n-slate-9'
                : campaign.title?.startsWith('[Default]')
                  ? 'bg-n-alpha-black4 text-n-slate-10'
                  : 'bg-kpt-amber/10 text-kpt-amber'
            "
          >
            {{
              campaign.campaign_status === 'archived'
                ? t('CAMPAIGN.ARCHIVE.ARCHIVED')
                : campaign.title?.startsWith('[Default]')
                  ? t('DIRECT.AUTO')
                  : t('DIRECT.CUSTOM')
            }}
          </span>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
